import React from "react";
import { SingleSelectQuestion } from "@labxchange/ui-components/lib/SingleSelectQuestion";
import { MultiSelectQuestion } from "@labxchange/ui-components/lib/MultiSelectQuestion";
import { OpenQuestion } from "@labxchange/ui-components/lib/OpenQuestion";
import { TableQuestion } from "@labxchange/ui-components/lib/TableQuestion";
import { Collapsible } from "@labxchange/ui-components/lib/LxCollapsible";
import { BodyText } from "@labxchange/ui-components/lib/BodyText";
import { Heading } from "@labxchange/ui-components/lib/Heading";
import { DataTable } from "@learningblocks/shared-components";
import { Utilities } from "@learningblocks/exp-constants";
const { COMPONENT_TYPE } = Utilities;
import { TranslationWrapper } from "@learningblocks/simulations/shared-components";
import { Component } from "../types";

export const Content = ({ components }: { components: Component[] }) => {
  const singleSelectQuestion = (component: Component) => {
    const [selectedOptionIndex, setSelectedOptionIndex] = React.useState<
      number | undefined
    >(undefined);

    return (
      <SingleSelectQuestion
        key={component.id}
        content={component.content as any}
        question={component.question as string}
        text={component.text as string}
        options={component.options as any}
        answerId={component.answerId as number[]}
        selectedOptionIndex={selectedOptionIndex}
        onOptionSelect={setSelectedOptionIndex}
        onSubmit={() => console.log("Single select submitted")}
        onReset={() => {
          setSelectedOptionIndex(undefined);
        }}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setCompleted={() => {}}
        totalAttempts={component.totalAttempts as number}
        isPredictionQuestion={component.isPredictionQuestion as boolean}
      />
    );
  };

  const multiSelectQuestion = (component: Component) => {
    const [selectedOptionIds, setSelectedOptionIds] = React.useState<number[]>(
      []
    );

    const handleOptionToggle = (optionId: number) => {
      setSelectedOptionIds((prev) => {
        if (prev.includes(optionId)) {
          return prev.filter((id) => id !== optionId);
        }
        return [...prev, optionId];
      });
    };

    return (
      <MultiSelectQuestion
        key={component.id}
        content={component.content as any}
        question={component.question as string}
        text={component.text as string}
        options={component.options as any}
        answerId={component.answerId as number[]}
        selectedOptionIds={selectedOptionIds}
        onOptionToggle={handleOptionToggle}
        onSubmit={() => console.log("Multi select submitted")}
        onReset={() => {
          setSelectedOptionIds([]);
        }}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setCompleted={() => {}}
        totalAttempts={component.totalAttempts as number}
        isPredictionQuestion={component.isPredictionQuestion as boolean}
      />
    );
  };

  const openQuestion = (component: Component) => {
    const [value, setValue] = React.useState("");

    return (
      <OpenQuestion
        key={component.id}
        question={component.question as string}
        answer={component.answer as string}
        value={value}
        onChange={setValue}
        onSubmit={(answer) => console.log("Open question submitted:", answer)}
        onReset={() => setValue("")}
        showAnswer={component.showAnswer as boolean}
        placeholder={component.placeholder as string}
        maxLength={component.maxLength as number}
        enableMath={component.enableMath as boolean}
      />
    );
  };

  const tableQuestion = (component: Component) => {
    const [data, setData] = React.useState(component.data as any);
    const [correct, setCorrect] = React.useState<boolean | null>(null);

    const handleCellChange = (params: any) => {
      setData((prevData: any) => {
        const newData = prevData.map((row: any, rowIdx: number) =>
          row.map((cell: any, colIdx: number) => {
            if (rowIdx === params.rowId && colIdx === params.colId) {
              return { ...cell, value: params.value };
            }
            return cell;
          })
        );
        return newData;
      });
    };

    const handleSubmit = () => {
      let allCorrect = true;
      data.forEach((row: any) => {
        row.forEach((cell: any) => {
          if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
            if (cell.answer !== undefined) {
              const value = cell.value;
              if (typeof cell.answer === "string") {
                if (String(value).toLowerCase() !== cell.answer.toLowerCase()) {
                  allCorrect = false;
                }
              } else if (typeof cell.answer === "number") {
                if (Number(value) !== cell.answer) {
                  allCorrect = false;
                }
              }
            }
          }
        });
      });

      setData((prevData: any) =>
        prevData.map((row: any) =>
          row.map((cell: any) => {
            if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
              return { ...cell, showFeedback: true };
            }
            return cell;
          })
        )
      );

      setCorrect(allCorrect);
    };

    const handleReset = () => {
      setData((prevData: any) =>
        prevData.map((row: any) =>
          row.map((cell: any) => {
            if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
              return { ...cell, value: null, showFeedback: false };
            }
            return cell;
          })
        )
      );
      setCorrect(null);
    };

    return (
      <TableQuestion
        key={component.id}
        id={component.id as any}
        type={(component.tableType as any) || "QUESTION_TABLE"}
        data={data}
        content={component.content as any}
        question={component.question as string}
        text={component.text as string}
        title={component.title as string}
        questionType={component.questionType as any}
        totalAttempts={component.totalAttempts as number}
        correct={correct}
        onCellChange={handleCellChange}
        submitAnswer={handleSubmit}
        onReset={handleReset}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setCompleted={() => {}}
        isTransparent={component.isTransparent as boolean}
        hintDetails={component.hintDetails as any}
        showFeedback={component.showFeedback as boolean}
      />
    );
  };

  const collapsible = (component: Component) => {
    return (
      <Collapsible
        key={component.id}
        label={component.label as string}
        content={component.content as any}
        open={component.open as boolean}
        variant={component.variant as any}
        locked={component.locked as boolean}
      />
    );
  };

  const bodyText = (component: Component) => {
    const getChildContent = (): React.ReactNode => {
      if (component.children !== undefined && component.children !== null) {
        return component.children as React.ReactNode;
      }
      if (component.text !== undefined && component.text !== null) {
        return typeof component.text === "object"
          ? JSON.stringify(component.text)
          : (component.text as React.ReactNode);
      }
      if (component.value !== undefined && component.value !== null) {
        return typeof component.value === "object"
          ? JSON.stringify(component.value)
          : (component.value as React.ReactNode);
      }
      return null;
    };

    return (
      <BodyText
        key={component.id}
        size={component.size as any}
        component={component.component as any}
        equation={component.equation as boolean}
      >
        {getChildContent()}
      </BodyText>
    );
  };

  const heading = (component: Component) => {
    const getChildContent = (): React.ReactNode => {
      if (component.children !== undefined && component.children !== null) {
        return component.children as React.ReactNode;
      }
      if (component.text !== undefined && component.text !== null) {
        return typeof component.text === "object"
          ? JSON.stringify(component.text)
          : (component.text as React.ReactNode);
      }
      if (component.value !== undefined && component.value !== null) {
        return typeof component.value === "object"
          ? JSON.stringify(component.value)
          : (component.value as React.ReactNode);
      }
      return null;
    };

    return (
      <Heading
        key={component.id}
        size={component.size as any}
        variant={component.variant as any}
        className={component.className as string}
      >
        {getChildContent()}
      </Heading>
    );
  };

  const dataTable = (component: Component) => {
    const [data, setData] = React.useState(component.data as any);

    React.useEffect(() => {
      setData(component.data as any);
    }, [component.data]);

    return (
      <DataTable
        key={component.id}
        data={data}
        uniqueResponses={component.uniqueResponses as any}
        className={component.className as string}
      />
    );
  };

  const renderComponent = (component: Component) => {
    const translatedComponent = TranslationWrapper({ component });
    switch (translatedComponent.type) {
      case COMPONENT_TYPE.CSCQ_NEW:
        return singleSelectQuestion(translatedComponent);
      case COMPONENT_TYPE.MSCQ_NEW:
        return multiSelectQuestion(translatedComponent);
      case COMPONENT_TYPE.OEQ_NEW:
        return openQuestion(translatedComponent);
      case COMPONENT_TYPE.TABLE_QUESTION:
        return tableQuestion(translatedComponent);
      case COMPONENT_TYPE.COLLAPSIBLE_NEW:
        return collapsible(translatedComponent);
      case COMPONENT_TYPE.BODY_TEXT:
        return bodyText(translatedComponent);
      case COMPONENT_TYPE.HEADING:
        return heading(translatedComponent);
      case COMPONENT_TYPE.DATA_TABLE:
        return dataTable(translatedComponent);

      default:
        return <h3> Component Type {translatedComponent.type}</h3>;
    }
  };

  return <>{components.map((component) => renderComponent(component))}</>;
};
